import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { SUBJECT_META, overallBand, BAND_STYLE } from "@/lib/scale";
import { ArrowLeft, Printer, Loader2, Sprout, Target, Check, Clock, AlertTriangle } from "lucide-react";
import DocHeader from "@/components/DocHeader";
import { ReviewBadge } from "@/components/ReviewBadge";

const STAGE_COLOR = {
  "Classroom Support": { bg: "#DCEBE4", text: "#3f7a5c" },
  "School Support": { bg: "#FBF0D6", text: "#9a7016" },
  "School Support Plus": { bg: "#FBE3E3", text: "#b04a4a" },
};

const PARENT_STATUS = {
  signed: { bg: "#DCEBE4", text: "#3f7a5c", label: "Parent signed", Icon: Check },
  awaiting: { bg: "#FBF0D6", text: "#9a7016", label: "Parent: awaiting", Icon: Clock },
  expired: { bg: "#FBE3E3", text: "#b04a4a", label: "Parent link expired", Icon: AlertTriangle },
};

const REPORT_STATUS = {
  signed: { bg: "#DCEBE4", text: "#3f7a5c", label: "Report signed", Icon: Check },
  awaiting: { bg: "#E7EDF2", text: "#44546A", label: "Report: awaiting", Icon: Clock },
  expired: { bg: "#FBE3E3", text: "#b04a4a", label: "Report link expired", Icon: AlertTriangle },
};

export default function ClassSummary() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/classes/${classId}/summary`).then((r) => setData(r.data)).catch(() => navigate("/"));
  }, [classId, navigate]);

  if (!data)
    return <div className="min-h-screen flex justify-center items-center text-slate-300"><Loader2 className="animate-spin" /></div>;

  const today = new Date().toLocaleDateString("en-IE", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#F9F9F7] py-8 px-4">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-3 mb-6 no-print flex-wrap">
        <button data-testid="summary-back-btn" onClick={() => navigate(`/class/${classId}`)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition">
          <ArrowLeft size={16} /> Back to class
        </button>
        <button data-testid="summary-print-btn" onClick={() => window.print()}
          className="bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2">
          <Printer size={17} /> Print / Save PDF
        </button>
      </div>

      <div className="print-area max-w-4xl mx-auto bg-white rounded-2xl shadow-[0_4px_24px_rgba(42,59,71,0.06)] p-8 sm:p-10">
        <DocHeader printOnly subtitle="Class Support Overview" />
        <div className="flex items-center justify-between border-b border-slate-100 pb-5">
          <div className="flex items-center gap-2.5">
            <span className="h-9 w-9 rounded-xl bg-[#81B29A] flex items-center justify-center text-white"><Sprout size={18} /></span>
            <span className="font-['Outfit'] font-bold text-lg text-slate-800">BloomTrack</span>
          </div>
          <p className="text-sm text-slate-400">{today}</p>
        </div>

        <h1 className="font-['Outfit'] text-3xl font-bold text-slate-800 mt-6">Class Support Summary</h1>
        <p className="text-slate-500 mt-1">{data.class_info?.name} · {data.class_info?.level}{data.class_info?.year ? ` · ${data.class_info.year}` : ""}</p>

        {data.students.length === 0 ? (
          <p className="text-sm text-slate-400 mt-8">No students in this class yet.</p>
        ) : (
          <div className="mt-6 divide-y divide-slate-100">
            {data.students.map((s) => {
              const sc = s.stage ? STAGE_COLOR[s.stage] : null;
              return (
                <div key={s.id} data-testid={`summary-student-${s.id}`} className="py-4 grid sm:grid-cols-[1fr_auto] gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <h3 className="font-['Outfit'] font-bold text-slate-800">{s.name}</h3>
                      {s.stage ? (
                        <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full" style={{ backgroundColor: sc.bg, color: sc.text }}>
                          {s.stage}
                        </span>
                      ) : (
                        <span className="text-[11px] text-slate-400">No support plan</span>
                      )}
                      <ReviewBadge review={s.review} testId={`summary-review-${s.id}`} />
                      {s.parent_plan && PARENT_STATUS[s.parent_plan.status] && (() => {
                        const p = PARENT_STATUS[s.parent_plan.status];
                        const I = p.Icon;
                        return (
                          <span data-testid={`summary-parent-${s.id}`} title={s.parent_plan.name ? `Signed by ${s.parent_plan.name}` : ""}
                            className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1" style={{ backgroundColor: p.bg, color: p.text }}>
                            <I size={11} /> {p.label}
                          </span>
                        );
                      })()}
                      {s.parent_report && REPORT_STATUS[s.parent_report.status] && (() => {
                        const p = REPORT_STATUS[s.parent_report.status];
                        const I = p.Icon;
                        return (
                          <span data-testid={`summary-report-${s.id}`} title={s.parent_report.name ? `Signed by ${s.parent_report.name}` : ""}
                            className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1" style={{ backgroundColor: p.bg, color: p.text }}>
                            <I size={11} /> {p.label}
                          </span>
                        );
                      })()}
                    </div>

                    {s.targets.length > 0 ? (
                      <ul className="mt-2 space-y-1">
                        {s.targets.map((t) => (
                          <li key={`${t.subject}-${t.area}-${t.text}`} className="flex items-start gap-2 text-sm text-slate-600">
                            <Target size={13} className="mt-1 shrink-0 text-[#b07d3a]" />
                            <span><span className="font-semibold text-slate-700">{t.subject} — {t.area}:</span> {t.text}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-slate-400 mt-1.5">No open priority targets</p>
                    )}
                  </div>

                  {/* Subject bands */}
                  <div className="flex sm:flex-col gap-1.5 sm:items-end">
                    {Object.keys(SUBJECT_META).map((k) => {
                      const b = s.bands[k];
                      const meta = SUBJECT_META[k];
                      return (
                        <div key={k} className="flex items-center gap-1.5 text-xs">
                          <span className="text-slate-400 w-20 text-right hidden sm:inline">{meta.name}</span>
                          {b ? (
                            <span className="font-semibold px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: BAND_STYLE[overallBand(b.total).key].soft, color: "#475569" }}>
                              {b.total}/45
                            </span>
                          ) : (
                            <span className="text-slate-300 px-2">—</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-8 pt-5 border-t border-slate-100 flex flex-wrap items-center gap-4 text-xs text-slate-500">
          <span className="font-semibold text-slate-600">Stages:</span>
          {Object.entries(STAGE_COLOR).map(([name, c]) => (
            <span key={name} className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded" style={{ backgroundColor: c.bg, border: `1px solid ${c.text}33` }} />
              {name}
            </span>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-6 text-center">
          BloomTrack · Class Support Summary · Aligned to the Irish primary curriculum
        </p>
      </div>
    </div>
  );
}
